import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { agentApi } from '@/api/agent.api';
import { channelApi } from '@/api/channel.api';
import { UserName } from '@/components/UserName';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs } from '@/components/ui/tabs';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { DescriptionText } from '@/components/ui/description-text';
import {
  ChevronLeftIcon,
  PencilIcon,
  RestartIcon,
  LoaderIcon,
  AlertCircleIcon,
} from '@/components/icons';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import InstanceTap from '@/pages/agent/AgentApps.tsx';
import TraceTab from '@/pages/agent/AgentTrace.tsx';
import FaultToleranceView from '@/components/Agents/FaultToleranceView';
import { parseFaultToleranceConfig } from '@/components/Agents/faultTolerance';
import {
  PLUGIN_TYPE_SANDBOX,
  PLUGIN_TYPE_SKILLGROUP,
  PLUGIN_TYPE_RAG,
  PLUGIN_TYPE_MEMORY,
  PLUGIN_TYPE_INFINI_MEMORY,
  PLUGIN_TYPE_MCP,
  PLUGIN_TYPE_TOOLKIT,
  PLUGIN_TYPE_USER_TOOLKIT,
  PLUGIN_TYPE_APPLICATION,
  PLUGIN_TYPE_SANDBOX_CLASS,
  PLUGIN_TYPE_SKILLGROUP_CLASS,
  PLUGIN_TYPE_RAG_CLASS,
  PLUGIN_TYPE_MEMORY_CLASS,
  PLUGIN_TYPE_MCP_CLASS,
} from '@/constants/plugin';
import {
  AccountStatus,
  ChannelType,
  ToolPermissionDefaultAction,
} from '@/proto/agentplatform/v1/channel_pb';
import { ListChannelAccountsRequestSchema } from '@/proto/agentplatform/httpv2/channel_pb';

function formatTimestamp(timestamp: number | bigint | undefined) {
  if (!timestamp) return '-';
  const date = new Date(Number(timestamp) / 1000000);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function channelTypeLabel(channelType: ChannelType): string {
  switch (channelType) {
    case ChannelType.FEISHU:
      return '飞书';
    case ChannelType.QQ:
      return 'QQ';
    case ChannelType.BRIDGE:
      return 'WebSocket';
    default:
      return ChannelType[channelType] ?? 'unknown';
  }
}

function toolPermissionDefaultActionLabel(action: ToolPermissionDefaultAction | undefined): string {
  switch (action) {
    case ToolPermissionDefaultAction.DENY:
      return '拒绝';
    case ToolPermissionDefaultAction.AUTO_APPROVE:
      return '自动批准';
    case ToolPermissionDefaultAction.REQUIRE_HUMAN:
      return '审批';
    case ToolPermissionDefaultAction.UNSPECIFIED:
    default:
      return '审批（未显式配置）';
  }
}

function pluginTypeLabel(pluginType: string): string {
  switch (pluginType) {
    case PLUGIN_TYPE_SANDBOX:
      return '沙箱实例';
    case PLUGIN_TYPE_SANDBOX_CLASS:
      return '沙箱模版';
    case PLUGIN_TYPE_SKILLGROUP:
      return '技能组实例';
    case PLUGIN_TYPE_SKILLGROUP_CLASS:
      return '技能组模版';
    case PLUGIN_TYPE_RAG:
      return '知识库实例';
    case PLUGIN_TYPE_RAG_CLASS:
      return '知识库模版';
    case PLUGIN_TYPE_MEMORY:
      return '记忆实例';
    case PLUGIN_TYPE_INFINI_MEMORY:
      return 'infini-memory';
    case PLUGIN_TYPE_MEMORY_CLASS:
      return '记忆模版';
    case PLUGIN_TYPE_MCP:
      return 'MCP 实例';
    case PLUGIN_TYPE_MCP_CLASS:
      return 'MCP 模版';
    case PLUGIN_TYPE_TOOLKIT:
      return '平台工具包';
    case PLUGIN_TYPE_USER_TOOLKIT:
      return '用户工具包';
    case PLUGIN_TYPE_APPLICATION:
      return '子代理';
    default:
      return pluginType;
  }
}

export default function AgentOverview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('instance');
  const [showRestartDialog, setShowRestartDialog] = useState(false);

  const {
    data: agent,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['agent', id],
    queryFn: () => agentApi.getAgent(id!),
    enabled: !!id,
  });

  // 渠道 tab 展示绑定到该模版的渠道账号（channel_accounts 表，含渠道管理页手工创建的），
  // 而非模版自身的 channelConfigs 派生配置——二者允许不一致。
  const {
    data: channelAccountsData,
    isLoading: channelAccountsLoading,
    error: channelAccountsError,
  } = useQuery({
    queryKey: ['agent-channel-accounts', id],
    queryFn: () =>
      channelApi.listChannelAccounts(
        create(ListChannelAccountsRequestSchema, { page: 1, pageSize: 100, agentId: id })
      ),
    enabled: !!id && activeTab === 'channel',
  });
  const channelAccounts = channelAccountsData?.items ?? [];

  const restartMutation = useMutation({
    mutationFn: () => agentApi.restartAgent(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', id] });
      toast.success('已重启，若模版参数已变更请手动更新实例', {
        icon: <RestartIcon className="h-4 w-4 text-primary" />,
      });
      setShowRestartDialog(false);
    },
    onError: (err) => {
      toast.error(`重启失败：${fullErrorText(err)}`, {
        icon: <AlertCircleIcon className="h-4 w-4 text-destructive" />,
      });
      setShowRestartDialog(false);
    },
  });

  const tabs = [
    { id: 'instance', label: '实例列表' },
    { id: 'trace', label: 'Trace' },
    { id: 'model', label: '模型' },
    { id: 'channel', label: '渠道' },
    { id: 'plugin', label: '插件' },
    { id: 'faultTolerance', label: '容错配置' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">加载失败</h3>
            <p className="text-muted-foreground mb-4">无法加载 Agent 信息</p>
            <Button variant="outline" onClick={() => navigate('/agents')}>
              返回列表
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/agents')}
            className="h-8 w-8"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">{agent.name}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowRestartDialog(true)}>
            <RestartIcon className="mr-2 h-4 w-4" />
            重启
          </Button>
          <Button variant="outline" onClick={() => navigate(`/agents/${id}/edit`)}>
            <PencilIcon className="mr-2 h-4 w-4" />
            编辑
          </Button>
          <Button onClick={() => navigate(`/agents/${id}/create-instance`)}>创建实例</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-3">
        <span className="text-sm">模版 ID: {agent.id}</span>
        <span className="text-sm">
          创建用户: <UserName userId={agent.userId} />
        </span>
        <span className="text-sm">创建时间: {formatTimestamp(agent.createdAt)}</span>
        <span className="text-sm">更新时间: {formatTimestamp(agent.updatedAt)}</span>
        <span className="text-sm flex items-center gap-1 min-w-0">
          描述: <DescriptionText text={agent.description} markdown />
        </span>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      <div className="animate-in fade-in duration-300">
        {activeTab === 'instance' && <InstanceTap />}
        {activeTab === 'trace' && <TraceTab />}
        {activeTab === 'model' && agent.modelConfig && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">模型信息</CardTitle>
              </CardHeader>
              <CardContent className="space-y-0 divide-y divide-border/50">
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm text-muted-foreground">模型名称</span>
                  <span className="text-sm font-medium">{agent.modelConfig?.modelName || '-'}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm text-muted-foreground">API Key</span>
                  <span className="font-mono text-sm">
                    {agent.modelConfig?.apiKey ? 'sk-••••••••••••••••' : '-'}
                  </span>
                </div>
                {agent.modelConfig?.systemPrompt && (
                  <div className="py-3">
                    <span className="text-sm text-muted-foreground block mb-2">系统提示词</span>
                    <div className="bg-muted/50 rounded-md p-3 text-sm leading-relaxed whitespace-pre-wrap max-h-80 overflow-auto">
                      {agent.modelConfig?.systemPrompt}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tool permissions */}
            {agent.advancedConfig?.toolPermissions && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">工具权限</CardTitle>
                  <CardDescription>
                    deny 优先 → allow → 无匹配则走默认行为（
                    {toolPermissionDefaultActionLabel(
                      agent.advancedConfig.toolPermissions.defaultAction
                    )}
                    ）
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-sm">
                    <span className="text-muted-foreground">默认行为：</span>
                    <span className="font-medium">
                      {toolPermissionDefaultActionLabel(
                        agent.advancedConfig.toolPermissions.defaultAction
                      )}
                    </span>
                  </div>
                  {agent.advancedConfig.toolPermissions.deny.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-destructive">拒绝规则 (deny)</h4>
                      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 space-y-2">
                        {agent.advancedConfig.toolPermissions.deny.map((r, i) => (
                          <div key={`deny-${i}`} className="flex items-center gap-4 text-sm">
                            <span
                              className="font-mono text-xs shrink-0 w-24 truncate"
                              title={r.toolName}
                            >
                              {r.toolName || '*'}
                            </span>
                            {r.argument && (
                              <span className="text-muted-foreground text-xs">
                                {r.argument}: <span className="font-mono">{r.pattern}</span>
                              </span>
                            )}
                            {!r.argument && r.pattern && (
                              <span className="font-mono text-xs">{r.pattern}</span>
                            )}
                            {!r.argument && !r.pattern && (
                              <span className="text-xs text-muted-foreground">无条件拒绝</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {agent.advancedConfig.toolPermissions.allow.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-green-600">允许规则 (allow)</h4>
                      <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-2 dark:border-green-800 dark:bg-green-950/30">
                        {agent.advancedConfig.toolPermissions.allow.map((r, i) => (
                          <div key={`allow-${i}`} className="flex items-center gap-4 text-sm">
                            <span
                              className="font-mono text-xs shrink-0 w-24 truncate"
                              title={r.toolName}
                            >
                              {r.toolName || '*'}
                            </span>
                            {r.argument && (
                              <span className="text-muted-foreground text-xs">
                                {r.argument}: <span className="font-mono">{r.pattern}</span>
                              </span>
                            )}
                            {!r.argument && r.pattern && (
                              <span className="font-mono text-xs">{r.pattern}</span>
                            )}
                            {!r.argument && !r.pattern && (
                              <span className="text-xs text-muted-foreground">无条件允许</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {activeTab === 'channel' && (
          <Card>
            <CardHeader>
              <CardTitle>渠道账号</CardTitle>
              <CardDescription>绑定到该模版的渠道账号</CardDescription>
            </CardHeader>
            <CardContent>
              {channelAccountsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10" />
                  <Skeleton className="h-10" />
                  <Skeleton className="h-10" />
                </div>
              ) : channelAccountsError ? (
                <p className="py-8 text-center text-sm text-destructive">
                  加载失败：{fullErrorText(channelAccountsError)}
                </p>
              ) : channelAccounts.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  暂无渠道账号 · 当前模版尚未关联渠道账号
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>渠道 ID / 名称</TableHead>
                      <TableHead>类型</TableHead>
                      <TableHead>状态</TableHead>
                      <TableHead>创建时间</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {channelAccounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <Link
                              to={`/channel-accounts/${account.id}`}
                              className="font-mono text-xs text-primary hover:underline"
                            >
                              {account.id}
                            </Link>
                            <span>{account.name || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{channelTypeLabel(account.type)}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {account.status === AccountStatus.ONLINE ? (
                              <Badge>在线</Badge>
                            ) : (
                              <Badge variant="secondary">离线</Badge>
                            )}
                            {account.disabled && <Badge variant="destructive">已停用</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatTimestamp(account.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
        {activeTab === 'plugin' && agent.pluginConfigs && agent.pluginConfigs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>插件配置</CardTitle>
              <CardDescription>Agent 关联的插件列表</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {agent.pluginConfigs.map((plugin, idx) => (
                  <div key={idx} className="rounded-lg border bg-muted/30 overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-border/40">
                      <Badge variant="outline">{pluginTypeLabel(plugin.pluginType)}</Badge>
                      <span className="text-sm font-medium">{plugin.name || '-'}</span>
                      {plugin.pluginUid && (
                        <span className="text-xs text-muted-foreground font-mono">
                          {plugin.pluginUid}
                        </span>
                      )}
                    </div>
                    {plugin.pluginType === PLUGIN_TYPE_USER_TOOLKIT &&
                      plugin.pluginParams?.length > 0 && (
                        <div className="px-4 py-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">参数名:</span>
                            <span className="text-xs font-mono">
                              {plugin.pluginParams[0]?.name || '-'}
                            </span>
                            <span className="text-xs text-muted-foreground ml-2">可配置:</span>
                            <span className="text-xs">
                              {plugin.pluginParams[0]?.configurable ? '是' : '否'}
                            </span>
                          </div>
                          {plugin.pluginParams[0]?.paramValue?.value && (
                            <div className="rounded-md border border-zinc-800 overflow-hidden">
                              <div className="flex bg-zinc-950">
                                <div
                                  className="select-none text-right pr-3 pl-3 py-2 text-xs font-mono leading-5 text-zinc-600 bg-zinc-900/50 border-r border-zinc-800"
                                  aria-hidden="true"
                                >
                                  {plugin.pluginParams[0].paramValue.value
                                    .split('\n')
                                    .map((_: string, i: number) => (
                                      <div key={i}>{i + 1}</div>
                                    ))}
                                </div>
                                <pre className="flex-1 bg-zinc-950 text-zinc-100 px-3 py-2 text-xs font-mono leading-5 overflow-auto max-h-60">
                                  {(() => {
                                    try {
                                      return JSON.stringify(
                                        JSON.parse(plugin.pluginParams[0].paramValue.value),
                                        null,
                                        2
                                      );
                                    } catch {
                                      return plugin.pluginParams[0].paramValue.value;
                                    }
                                  })()}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {activeTab === 'faultTolerance' && (
          <FaultToleranceView
            value={parseFaultToleranceConfig(agent.advancedConfig?.faultTolerance)}
            showConfigurable
          />
        )}
      </div>

      <AlertDialog open={showRestartDialog} onOpenChange={setShowRestartDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>重启智能体模版</AlertDialogTitle>
            <AlertDialogDescription>
              重启将同步最新配置到运行实例，若模版参数已变更，需手动更新实例以使配置生效。确认重启「
              {agent?.name}」？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <Button onClick={() => restartMutation.mutate()} disabled={restartMutation.isPending}>
              {restartMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}
              确认重启
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
